import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { adminApi, companyApi } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
  selectUserRole,
  selectCompanyId,
  selectIsAdmin,
} from '../../store/slices/authSlice';

/* ── Edit inline form ───────────────────────────────────────────────────── */
function EditUserForm({ user, assignableRoles, onCancel, onSave, isSaving }) {
  // Resolve phone regardless of backend field name
  const resolvedPhone = user.phone ?? user.phoneNumber ?? user.phone_number ?? user.mobile ?? '';

  // Ensure the user's current role is always available as an option
  const roleOptions = assignableRoles.includes(user.role)
    ? assignableRoles
    : [user.role, ...assignableRoles].filter(Boolean);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      firstName: user.firstName ?? '',
      lastName:  user.lastName  ?? '',
      phone:     resolvedPhone,
      role:      user.role ?? '',
    },
  });

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="font-semibold text-gray-900 mb-4">Edit User</h2>
      <form onSubmit={handleSubmit(onSave)} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="First Name *" error={errors.firstName?.message}>
          <input
            {...register('firstName', {
              required: 'First name is required',
              minLength: { value: 2, message: 'At least 2 characters required' },
              maxLength: { value: 50, message: 'Max 50 characters' },
              pattern: { value: /^[a-zA-Z]+$/, message: 'Only letters allowed — no numbers, spaces or special characters' },
              setValueAs: (v) => v?.trim(),
            })}
            className={inputCls(errors.firstName)}
            placeholder="John"
            maxLength={50}
          />
        </Field>

        <Field label="Last Name *" error={errors.lastName?.message}>
          <input
            {...register('lastName', {
              required: 'Last name is required',
              minLength: { value: 2, message: 'At least 2 characters required' },
              maxLength: { value: 50, message: 'Max 50 characters' },
              pattern: { value: /^[a-zA-Z]+$/, message: 'Only letters allowed — no numbers, spaces or special characters' },
              setValueAs: (v) => v?.trim(),
            })}
            className={inputCls(errors.lastName)}
            placeholder="Doe"
            maxLength={50}
          />
        </Field>

        <Field label="Email">
          <input
            value={user.email ?? ''}
            readOnly
            disabled
            className="block w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-400 cursor-not-allowed"
          />
        </Field>

        <Field label="Phone *" error={errors.phone?.message}>
          <input
            {...register('phone', {
              required: 'Phone number is required',
              maxLength: { value: 20, message: 'Max 20 characters' },
              pattern: {
                value: /^\+?[\d\s\-().]{7,20}$/,
                message: 'Enter a valid phone number (e.g. +1 555 000 0000)',
              },
              setValueAs: (v) => v?.trim(),
              validate: (v) => {
                const norm = (s) => (s ?? '').replace(/[\s\-().+]/g, '');
                const digits = norm(v);
                if (digits.length < 7)  return 'Phone number too short';
                if (digits.length > 15) return 'Phone number too long (max 15 digits, E.164)';
                return true;
              },
            })}
            className={inputCls(errors.phone)}
            placeholder="+1 555 000 0000"
            maxLength={20}
          />
        </Field>

        <Field label="Role *" error={errors.role?.message}>
          <select
            {...register('role', { required: 'Please select a role' })}
            className={inputCls(errors.role)}
          >
            <option value="">Select role…</option>
            {roleOptions.map((r) => (
              <option key={r} value={r}>{roleLabel(r)}</option>
            ))}
          </select>
        </Field>

        <div className="sm:col-span-2 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving || !isValid || !isDirty}
            title={!isDirty ? 'No changes to save' : !isValid ? 'Fix errors before saving' : undefined}
            className="px-6 py-2 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ── Role option sets per context ──────────────────────────────────────── */
const PLATFORM_ROLES_SUPER = ['PLATFORM_ADMIN', 'PLATFORM_USER', 'PLATFORM_VIEWER', 'CLIENT_VENDOR_ADMIN'];
const CLIENT_ROLES          = ['CLIENT_USER', 'CLIENT_VIEWER'];
const VENDOR_ROLES          = ['VENDOR_USER', 'VENDOR_VIEWER'];


/* Role badge colours */
const ROLE_COLOURS = {
  PLATFORM: 'bg-violet-50 text-violet-700',
  CLIENT:   'bg-blue-50   text-blue-700',
  VENDOR:   'bg-amber-50  text-amber-700',
};

function roleBadgeCls(role = '') {
  if (role.startsWith('PLATFORM_')) return ROLE_COLOURS.PLATFORM;
  if (role.startsWith('CLIENT_'))   return ROLE_COLOURS.CLIENT;
  if (role.startsWith('VENDOR_'))   return ROLE_COLOURS.VENDOR;
  return 'bg-gray-100 text-gray-600';
}

/** Human-readable label: CLIENT_ADMIN → "Client Admin" */
function roleLabel(role = '') {
  const map = {
    PLATFORM_SUPER_ADMIN: 'Super Admin',
    PLATFORM_ADMIN:       'Platform Admin',
    PLATFORM_USER:        'Platform User',
    PLATFORM_VIEWER:      'Platform Viewer',
    CLIENT_VENDOR_ADMIN:  'Client-Vendor Admin',
    CLIENT_ADMIN:         'Account Admin',
    CLIENT_USER:          'Account User',
    CLIENT_VIEWER:        'Account Viewer',
    VENDOR_ADMIN:         'Vendor Admin',
    VENDOR_USER:          'Vendor User',
    VENDOR_VIEWER:        'Vendor Viewer',
  };
  return map[role] ?? role;
}

/* ── Reusable form field ────────────────────────────────────────────────── */
function Field({ label, error, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

const inputCls = (error) =>
  `block w-full px-3 py-2 border ${error ? 'border-red-400 bg-red-50 focus:ring-red-400' : 'border-gray-300 focus:ring-blue-500'} rounded-lg text-sm focus:outline-none focus:ring-2`;

/* ── Edit User Modal ────────────────────────────────────────────────────── */
function EditUserModal({ user, assignableRoles, onClose, onSave, isSaving }) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      firstName: user.firstName ?? '',
      lastName:  user.lastName  ?? '',
      phone:     user.phone     ?? '',
      role:      user.role      ?? '',
    },
  });

  // Reset form if a different user is opened
  useEffect(() => {
    reset({
      firstName: user.firstName ?? '',
      lastName:  user.lastName  ?? '',
      phone:     user.phone     ?? '',
      role:      user.role      ?? '',
    });
  }, [user.id, reset]);

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">Edit User</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Read-only email */}
        <div className="mb-4 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500">
          <span className="font-medium text-gray-700">Email: </span>{user.email}
        </div>

        <form onSubmit={handleSubmit(onSave)} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="First Name *" error={errors.firstName?.message}>
            <input
              {...register('firstName', {
                required: 'First name is required',
                minLength: { value: 2, message: 'At least 2 characters' },
                maxLength: { value: 50, message: 'Max 50 characters' },
                pattern: { value: /^[a-zA-Z]+$/, message: 'Letters only' },
                setValueAs: (v) => v?.trim(),
              })}
              className={inputCls(errors.firstName)}
              placeholder="John"
              maxLength={50}
            />
          </Field>

          <Field label="Last Name *" error={errors.lastName?.message}>
            <input
              {...register('lastName', {
                required: 'Last name is required',
                minLength: { value: 2, message: 'At least 2 characters' },
                maxLength: { value: 50, message: 'Max 50 characters' },
                pattern: { value: /^[a-zA-Z]+$/, message: 'Letters only' },
                setValueAs: (v) => v?.trim(),
              })}
              className={inputCls(errors.lastName)}
              placeholder="Doe"
              maxLength={50}
            />
          </Field>

          <Field label="Phone *" error={errors.phone?.message}>
            <input
              {...register('phone', {
                required: 'Phone number is required',
                maxLength: { value: 20, message: 'Max 20 characters' },
                pattern: {
                  value: /^\+?[\d\s\-().]{7,20}$/,
                  message: 'Enter a valid phone number (e.g. +1 555 000 0000)',
                },
                setValueAs: (v) => v?.trim(),
                validate: (v) => {
                  const norm = (s) => (s ?? '').replace(/[\s\-().+]/g, '');
                  const digits = norm(v);
                  if (digits.length < 7)  return 'Phone number too short';
                  if (digits.length > 15) return 'Phone number too long (max 15 digits)';
                  return true;
                },
              })}
              className={inputCls(errors.phone)}
              placeholder="+1 555 000 0000"
              maxLength={20}
            />
          </Field>

          <Field label="Role *" error={errors.role?.message}>
            <select
              {...register('role', { required: 'Please select a role' })}
              className={inputCls(errors.role)}
            >
              <option value="">Select role…</option>
              {assignableRoles.map((r) => (
                <option key={r} value={r}>{roleLabel(r)}</option>
              ))}
            </select>
          </Field>

          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || !isValid || !isDirty}
              title={!isDirty ? 'No changes to save' : !isValid ? 'Fix errors before saving' : undefined}
              className="px-6 py-2 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Main component ─────────────────────────────────────────────────────── */
export default function UserManagement() {
  const qc           = useQueryClient();
  const role         = useSelector(selectUserRole);
  const companyId    = useSelector(selectCompanyId);
  const isAdmin      = useSelector(selectIsAdmin);

  const [showForm, setShowForm]       = useState(false);
  const [editingUser, setEditingUser] = useState(null); // user object being edited
  const [auditUser, setAuditUser]     = useState(null); // user whose audit log is open

  const { register, handleSubmit, reset, formState: { errors, isValid } } = useForm({ mode: 'onChange' });

  /* ── Determine context ───────────────────────────────────────────────── */
  const isPlatform    = role?.startsWith('PLATFORM_') || role === 'CLIENT_VENDOR_ADMIN';
  const isClientAdmin = role === 'CLIENT_ADMIN';
  const isVendorAdmin = role === 'VENDOR_ADMIN';

  const assignableRoles = isPlatform
    ? PLATFORM_ROLES_SUPER
    : isClientAdmin
    ? CLIENT_ROLES
    : isVendorAdmin
    ? VENDOR_ROLES
    : [];

  const canCreate = isAdmin;
  const canToggle = isAdmin;
  const canEdit   = isAdmin;

  const canActOnUser = (u) => {
    if (isPlatform) return true;
    return u.role && !u.role.endsWith('_ADMIN') && u.role !== 'CLIENT_VENDOR_ADMIN';
  };

  /* ── Data fetching ───────────────────────────────────────────────────── */
  const queryKey = isPlatform
    ? ['allUsers', 'PLATFORM']
    : ['companyUsers', companyId];

  const { data, isLoading } = useQuery(
    queryKey,
    () =>
      isPlatform
        ? adminApi.getAllUsers({ group: 'PLATFORM', size: 100 })
        : companyApi.getCompanyUsers(companyId, { size: 100 }),
    { keepPreviousData: true, enabled: isPlatform || !!companyId }
  );

  const { data: auditData, isLoading: auditLoading } = useQuery(
    ['userAuditLog', auditUser?.id],
    () => adminApi.getUserAuditLog(auditUser.id),
    { enabled: !!auditUser && isPlatform }
  );
  const auditEntries = auditData?.data ?? [];

  /* ── Mutations ───────────────────────────────────────────────────────── */
  const createMutation = useMutation(
    (formData) =>
      isPlatform
        ? adminApi.createPlatformUser(formData)
        : companyApi.createCompanyUser(companyId, formData),
    {
      onSuccess: () => {
        toast.success('User created successfully!');
        qc.invalidateQueries(queryKey);
        setShowForm(false);
        reset();
      },
      onError: (err) =>
        toast.error(err.response?.data?.error || 'Failed to create user'),
    }
  );

  const updateMutation = useMutation(
    ({ userId, userCompanyId, formData }) => {
      if (isPlatform && !userCompanyId) {
        // Platform user — use admin endpoint
        return adminApi.updatePlatformUser(userId, formData);
      }
      // Company user (client or vendor) — use company-scoped endpoint
      const cid = userCompanyId || companyId;
      return companyApi.updateCompanyUser(cid, userId, formData);
    },
    {
      onSuccess: () => {
        toast.success('User updated successfully!');
        qc.invalidateQueries(queryKey);
        setEditingUser(null);
      },
      onError: (err) =>
        toast.error(err.response?.data?.error || 'Failed to update user'),
    }
  );

  const toggleMutation = useMutation(
    ({ userId, userCompanyId }) => {
      if (!isPlatform) {
        return companyApi.toggleCompanyUserActive(companyId, userId);
      }
      if (userCompanyId) {
        return companyApi.toggleCompanyUserActive(userCompanyId, userId);
      }
      return adminApi.toggleUserActive(userId);
    },
    {
      onSuccess: () => {
        toast.success('User status updated');
        qc.invalidateQueries(queryKey);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to update status'),
    }
  );

  /* ── Derive users list ───────────────────────────────────────────────── */
  const users =
    data?.data?.content ??
    data?.content ??
    (Array.isArray(data?.data) ? data.data : []);

  const atUserLimit = false;

  /* ── Submit handlers ─────────────────────────────────────────────────── */
  const onSubmit = (d) => createMutation.mutate(d);

  const onEditSave = (formData) => {
    if (!editingUser) return;
    updateMutation.mutate({
      userId:        editingUser.id,
      userCompanyId: editingUser.companyId,
      formData:      { ...formData, email: editingUser.email },
    });
  };

  /* ── Page meta ───────────────────────────────────────────────────────── */
  const pageTitle = isPlatform ? 'OmniDrive Users' : 'Team Members';
  const pageDesc  = isPlatform
    ? 'OmniDrive users only (Accounts and Vendors are managed via Companies)'
    : isClientAdmin
    ? 'Manage your client company users'
    : 'Manage your vendor company users';

  const tableHeaders = ['Name', 'Email', 'Role', 'Status',
    ...(canToggle || canEdit ? ['Actions'] : [])];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{pageTitle}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{pageDesc}</p>
        </div>

        {canCreate && (
          <button
            onClick={() => { setShowForm(!showForm); setEditingUser(null); }}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            {showForm ? 'Cancel' : '+ Add User'}
          </button>
        )}
      </div>

      {/* Viewer notice (read-only roles) */}
      {!canCreate && !isClientAdmin && !isVendorAdmin && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg px-4 py-3">
          You have view-only access. Contact your administrator to add or modify users.
        </div>
      )}

      {/* Create User Form */}
      {showForm && canCreate && !atUserLimit && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-900 mb-4">
            {isPlatform ? 'Create Platform User' : 'Create Team Member'}
          </h2>
          <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="First Name *" error={errors.firstName?.message}>
              <input {...register('firstName', {
                required: 'First name is required',
                minLength: { value: 2, message: 'At least 2 characters required' },
                maxLength: { value: 50, message: 'Max 50 characters' },
                pattern: { value: /^[a-zA-Z]+$/, message: 'Only letters allowed — no numbers, spaces or special characters' },
                setValueAs: (v) => v?.trim(),
              })} className={inputCls(errors.firstName)} placeholder="John" maxLength={50} autoComplete="given-name" />
            </Field>

            <Field label="Last Name *" error={errors.lastName?.message}>
              <input {...register('lastName', {
                required: 'Last name is required',
                minLength: { value: 2, message: 'At least 2 characters required' },
                maxLength: { value: 50, message: 'Max 50 characters' },
                pattern: { value: /^[a-zA-Z]+$/, message: 'Only letters allowed — no numbers, spaces or special characters' },
                setValueAs: (v) => v?.trim(),
              })} className={inputCls(errors.lastName)} placeholder="Doe" maxLength={50} autoComplete="family-name" />
            </Field>

            <Field label="Email *" error={errors.email?.message}>
              <input {...register('email', {
                required: 'Email is required',
                maxLength: { value: 254, message: 'Email must not exceed 254 characters' },
                pattern: {
                  value: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/,
                  message: 'Enter a valid email address',
                },
                setValueAs: (v) => v?.trim().toLowerCase(),
                validate: (v) =>
                  !users.some(u => u.email?.toLowerCase().trim() === v?.toLowerCase().trim())
                    || 'This email is already in use',
              })} type="email" className={inputCls(errors.email)} placeholder="john@example.com" maxLength={254} autoComplete="email" />
            </Field>

            <Field label="Password *" error={errors.password?.message}>
              <input {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Minimum 8 characters' },
                maxLength: { value: 64, message: 'Max 64 characters' },
                pattern: {
                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]).{8,}$/,
                  message: 'Must contain uppercase, lowercase, a number and a special character',
                },
              })} type="password" className={inputCls(errors.password)} placeholder="Min. 8 characters" maxLength={64} autoComplete="new-password" />
            </Field>

            <Field label="Phone *" error={errors.phone?.message}>
              <input {...register('phone', {
                required: 'Phone number is required',
                maxLength: { value: 20, message: 'Max 20 characters' },
                pattern: {
                  value: /^\+?[\d\s\-().]{7,20}$/,
                  message: 'Enter a valid phone number (e.g. +1 555 000 0000)',
                },
                setValueAs: (v) => v?.trim(),
                validate: (v) => {
                  const norm = (s) => (s ?? '').replace(/[\s\-().+]/g, '');
                  const digits = norm(v);
                  if (digits.length < 7)  return 'Phone number too short';
                  if (digits.length > 15) return 'Phone number too long (max 15 digits, E.164)';
                  return !users.some(u => u.phone && norm(u.phone) === digits)
                    || 'This phone number is already in use';
                },
              })} className={inputCls(errors.phone)} placeholder="+1 555 000 0000" maxLength={20} autoComplete="tel" />
            </Field>

            <Field label="Role *" error={errors.role?.message}>
              <select {...register('role', { required: 'Please select a role' })} className={inputCls(errors.role)}>
                <option value="">Select role…</option>
                {assignableRoles.map((r) => (
                  <option key={r} value={r}>{roleLabel(r)}</option>
                ))}
              </select>
            </Field>

            <div className="sm:col-span-2 flex justify-end">
              <button
                type="submit"
                disabled={createMutation.isLoading || !isValid}
                title={!isValid ? 'Fill all required fields correctly before creating a user' : undefined}
                className="px-6 py-2 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                {createMutation.isLoading ? 'Creating…' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit User Form — inline, same style as Create */}
      {editingUser && (
        <EditUserForm
          key={editingUser.id}
          user={editingUser}
          assignableRoles={assignableRoles}
          onCancel={() => setEditingUser(null)}
          onSave={onEditSave}
          isSaving={updateMutation.isLoading}
        />
      )}

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-x-auto">
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {tableHeaders.map((h) => (
                  <th key={h}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.length === 0 ? (
                <tr>
                  <td colSpan={tableHeaders.length}
                    className="px-4 py-12 text-center text-gray-400">
                    No users found.
                    {canCreate && ' Use the "+ Add User" button above to create one.'}
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {u.fullName || `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${roleBadgeCls(u.role)}`}>
                        {roleLabel(u.role)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        u.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {u.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {(canToggle || canEdit) && (
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {/* Edit button */}
                          {canEdit && canActOnUser(u) ? (
                            <button
                              onClick={() => { setEditingUser(u); setShowForm(false); }}
                              className="text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
                            >
                              Edit
                            </button>
                          ) : canEdit ? (
                            <span className="text-xs text-gray-300" title="Only OmniDrive can edit admin accounts">—</span>
                          ) : null}

                          {/* Role history button — platform admins only */}
                          {isPlatform && (
                            <button
                              onClick={() => setAuditUser(auditUser?.id === u.id ? null : u)}
                              className={`text-xs font-medium transition-colors ${
                                auditUser?.id === u.id
                                  ? 'text-violet-700 underline'
                                  : 'text-violet-500 hover:text-violet-700'
                              }`}
                            >
                              History
                            </button>
                          )}

                          {/* Toggle active button */}
                          {canToggle && canActOnUser(u) ? (
                            <button
                              onClick={() => toggleMutation.mutate({ userId: u.id, userCompanyId: u.companyId })}
                              disabled={toggleMutation.isLoading}
                              className={`text-xs font-medium transition-colors ${
                                u.active ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'
                              }`}
                            >
                              {u.active ? 'Deactivate' : 'Activate'}
                            </button>
                          ) : canToggle ? (
                            <span className="text-xs text-gray-300" title="Only OmniDrive can deactivate admin accounts">—</span>
                          ) : null}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Row count */}
      {!isLoading && users.length > 0 && (
        <p className="text-xs text-gray-400 text-right">
          Showing {users.length} user{users.length !== 1 ? 's' : ''}
        </p>
      )}

      {/* Role Change History Panel */}
      {auditUser && isPlatform && (
        <div className="bg-white rounded-xl border border-violet-200 shadow-sm overflow-x-auto">
          <div className="flex items-center justify-between px-4 py-3 bg-violet-50 border-b border-violet-100">
            <div>
              <h3 className="text-sm font-semibold text-violet-900">Role Change History</h3>
              <p className="text-xs text-violet-600 mt-0.5">
                {auditUser.firstName} {auditUser.lastName} · {auditUser.email}
              </p>
            </div>
            <button
              onClick={() => setAuditUser(null)}
              className="text-violet-400 hover:text-violet-600 text-lg leading-none"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {auditLoading ? (
            <div className="px-4 py-6 text-center text-sm text-gray-400">Loading…</div>
          ) : auditEntries.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-gray-400">
              No role changes recorded for this user.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Date & Time', 'Changed By', 'Details'].map((h) => (
                    <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[...auditEntries].reverse().map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                      {entry.performedAt
                        ? new Date(entry.performedAt).toLocaleString()
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-700 text-xs">{entry.performedBy}</td>
                    <td className="px-4 py-3 text-gray-700 text-xs">{entry.details}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
